'use client';

import { useEffect, useRef, useState } from 'react';
import { formatCredits } from '@/lib/balance/units';
import { useT } from '@/components/i18n-provider';
import { AlertTriangle, ArrowLeft, X } from 'lucide-react';
import type { Notification } from '@/lib/notifications/service';
import { BottomSheetShell } from '@/components/ui/bottom-sheet-shell';
import {
  ToastStackPortal,
  useToastStackTarget,
} from '@/components/workspace/toast-stack';
import { cn } from '@/lib/utils';

// Modal-style toast for critical (action-required) notifications when the
// user is in-app but not focused on the matching chat. Sits in the
// bottom-left "system nudge" lane (matches PasskeyPrompt / CookieConsent /
// PushPrompt / DiscordInstallContinueBanner), focus-trapped, and chimes
// once on first appearance. The chime is a tiny generated WebAudio
// click — no asset to ship. No mute toggle: the chime is a single
// transient cue per toast appearance, not an ongoing ring, so a
// per-toast mute UI was visual clutter for a problem nobody had.

type CriticalAction =
  | 'approve'
  | 'deny'
  | 'always_approve'
  | 'approve_once'
  | 'reject';

interface Props {
  notification: Notification;
  /**
   * Action dispatcher. The optional `threshold` is a dollar amount
   * supplied by the "Set limit" picker (`always_approve` only) — 2x /
   * 10x quick-pick or a custom value typed by the user. The overlay
   * forwards it to the PATCH endpoint, which clamps it to `>= amount`
   * and uses it to create the spending override; absent threshold
   * falls back to the legacy 5x default.
   */
  onAction: (action: CriticalAction, threshold?: number) => void;
  onDismiss: () => void;
  onView?: () => void; // navigate to chat
}

/** Picker button labels. Delegates to the one formatter in
 *  `lib/balance/units.ts`: amounts are shown in credits, never dollars. */
const fmtMoney = formatCredits;

function playChime() {
  try {
    if (typeof window === 'undefined') return;
    const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.18);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.22);
    osc.start();
    osc.stop(ctx.currentTime + 0.25);
    setTimeout(() => { try { ctx.close(); } catch {} }, 400);
  } catch { /* chime is best-effort */ }
}

export function CriticalToast({ notification, onAction, onDismiss, onView }: Props) {
  const t = useT();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const customInputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Set-limit picker state. `picker = 'actions'` shows the default
  // [Approve once] [Set limit] [Deny] row; `'quick'` swaps it for
  // [2x] [10x] [Custom] [←]; `'custom'` swaps in a numeric input +
  // [Apply] [←]. The back affordance is INLINE in the same row (a
  // small left-arrow icon), not a separate trailing chip, so the
  // toast height doesn't grow when the user enters the sub-pickers.
  const [picker, setPicker] = useState<'actions' | 'quick' | 'custom'>(
    'actions',
  );
  const [customStr, setCustomStr] = useState('');

  useEffect(() => {
    playChime();
    const previouslyFocused = typeof document !== 'undefined' ? document.activeElement as HTMLElement | null : null;
    const focusable = containerRef.current?.querySelector<HTMLElement>('button, [href]');
    focusable?.focus();
    return () => { previouslyFocused?.focus?.(); };
  }, [notification.id]);

  // Focus trap: keep TAB inside the toast.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss();
        return;
      }
      if (e.key !== 'Tab' || !containerRef.current) return;
      const focusables = containerRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  const data = (notification.data as Record<string, unknown> | null) ?? {};
  const amount = data.amount as string | undefined;
  const chatId = data.chatId as string | undefined;
  // Dollar-string daily cap that triggered this notification (only
  // present for `reason: 'daily_cap'` payment_required notifications
  // from lib/agents/router.ts). When present, drives the 2x / 10x
  // quick-pick multipliers so they suggest meaningful new caps
  // ($5 → $10 / $50) instead of trivial per-request multiples.
  const dailyLimitStr =
    typeof data.dailyLimit === 'string' ? (data.dailyLimit as string) : undefined;

  const isPayment = notification.type === 'payment_required';
  const isToolApproval = notification.type === 'tool_approval';
  const isPermissionRequest = notification.type === 'widget_permission_request';
  // An agent asking to publish an app (2026-09-06). Same critical rail as a
  // permission request, different stakes: approving makes the app public and
  // the direct link outlives an unpublish, so the primary button says what
  // it does ("Publish") rather than a generic Approve.
  const isPublishRequest = notification.type === 'widget_publish_request';
  const permissionFeature =
    typeof data.feature === 'string' ? (data.feature as string) : undefined;
  // Agent-control requests carry `agentName` instead of `feature` — same
  // notification type, same buttons, so the label just reads whichever is set.
  const permissionAgentName =
    typeof data.agentName === 'string' && data.agentName
      ? (data.agentName as string)
      : undefined;
  const toolName =
    typeof data.toolName === 'string' ? (data.toolName as string) : undefined;
  const serverName =
    typeof data.serverName === 'string'
      ? (data.serverName as string)
      : undefined;
  const args = data.args;

  async function take(action: CriticalAction, threshold?: number) {
    if (busy) return;
    setBusy(action);
    try {
      await Promise.resolve(onAction(action, threshold));
    } finally {
      setBusy(null);
    }
  }

  // When the user opens the custom-amount sub-picker, autofocus the
  // input so they can start typing immediately without a hop to the
  // mouse. Run on every transition INTO 'custom' so re-entering
  // (back → custom) re-focuses too.
  useEffect(() => {
    if (picker === 'custom') {
      // rAF to wait for the input to mount + paint before focus.
      requestAnimationFrame(() => customInputRef.current?.focus());
    }
  }, [picker]);

  // Parse the request amount + the optional caller-supplied spend cap
  // (e.g. `dailyLimit` on a `reason: 'daily_cap'` notification).
  //
  // BASE FOR 2x / 10x: prefer the SPEND LIMIT that was hit (e.g. $5
  // daily cap → $10 / $50) over the per-request amount (e.g. $0.03 →
  // $0.06 / $0.30). The latter would suggest meaninglessly tiny new
  // thresholds for daily-cap notifications. When the notification
  // carries no limit (insufficient-balance, tool-cost, generic agent
  // request) we fall back to the request amount.
  const amountNum = (() => {
    const n = typeof amount === 'string' ? parseFloat(amount) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 0;
  })();
  const dailyLimitNum = (() => {
    const n =
      typeof dailyLimitStr === 'string' ? parseFloat(dailyLimitStr) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 0;
  })();
  const pickerBase = dailyLimitNum > 0 ? dailyLimitNum : amountNum;
  const quick2x = pickerBase * 2;
  const quick10x = pickerBase * 10;

  // Two-track positioning, keyed off the shared ToastStack portal:
  //
  //   - Desktop (matchMedia min-width 768px): portals into the
  //     `critical` slot of `ToastStackProvider` so the toast sits
  //     at the top of the top-left prompt column under the
  //     BrandCard, above any non-critical prompts already there.
  //   - Mobile / pre-hydration: hand-rolled docked sheet at
  //     viewport bottom, full width, with the existing
  //     `radiusMode="docked-sm-rounded"` + drag handle.
  //
  // The soft red halo (`drop-shadow` filter, alpha 0.16) signals
  // criticality on desktop; mobile sheet has no exterior to glow
  // into. Tone is deliberately restrained so the cue reads as
  // "this needs attention" rather than an alarm — the criticality
  // is already carried by the red icon-tint and action-button
  // copy inside the body.
  const desktopTarget = useToastStackTarget('critical');
  const wrapperClassName = desktopTarget
    ? cn(
        'animate-in slide-in-from-top-4 fade-in duration-200',
        'drop-shadow-[0_0_20px_rgba(239,68,68,0.16)]',
      )
    : cn(
        'fixed inset-x-0 bottom-0 z-[300] pointer-events-auto',
        'animate-in slide-in-from-bottom fade-in duration-200',
      );

  // Header. Mirrors the biometric / passkey prompt's icon style: a
  // 10×10 rounded circle with a tinted background and the identity
  // glyph at 5×5 inside. For criticals we tint the circle red — same
  // family as the wrapper halo — so the urgency cue is reinforced
  // at the icon level instead of relying on a separate amber square.
  const header = (
    <div className="flex items-start gap-3 px-4 pt-4">
      <div className="h-10 w-10 shrink-0 rounded-full bg-red-500/12 dark:bg-red-500/15 text-red-600 dark:text-red-400 flex items-center justify-center">
        <AlertTriangle className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div
          id={`critical-toast-${notification.id}-title`}
          className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 leading-tight"
        >
          {notification.title}
        </div>
        {notification.body && (
          <div
            id={`critical-toast-${notification.id}-body`}
            // Never clamped, on any type: the toast card grows to its content
            // and the 384px desktop column has no height cap, so the whole
            // body is on screen when the buttons are. Pinned by a test after
            // 2026-09-07, when the BELL's two-line clamp cut a publish warning
            // exactly at the sentence saying an unpublish does not close the
            // link. Publish bodies additionally read at full contrast: that
            // text is what the decision rests on.
            className={cn(
              'text-xs mt-1 leading-relaxed',
              isPublishRequest
                ? 'text-zinc-800 dark:text-zinc-200'
                : 'text-zinc-600 dark:text-zinc-400',
            )}
          >
            {notification.body}
          </div>
        )}
        {isToolApproval && (serverName || toolName) && (
          <div className="text-[11px] text-zinc-500 dark:text-zinc-500 mt-1.5 font-mono break-all">
            {serverName ? `${serverName}.` : ''}
            {toolName ?? 'tool'}
          </div>
        )}
        {isToolApproval && args !== undefined && args !== null && (
          <pre className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-2 max-h-28 overflow-auto bg-zinc-50 dark:bg-zinc-800/50 rounded p-2 font-mono">
            {(() => {
              try {
                return JSON.stringify(args, null, 2);
              } catch {
                return '<unserializable args>';
              }
            })()}
          </pre>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('actions.dismiss')}
        className="-mr-1 -mt-1 text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300 p-1 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );

  // Compact back-arrow icon button shared by the 'quick' and 'custom'
  // sub-pickers. Sits inline at the start of the row instead of a
  // separate trailing chip — keeps the picker single-row and shrinks
  // the toast's overall height.
  const backButton = (target: 'actions' | 'quick') => (
    <button
      type="button"
      onClick={() => setPicker(target)}
      disabled={busy !== null}
      aria-label={t('user.back')}
      className="inline-flex items-center justify-center h-7 w-7 rounded-md text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
    >
      <ArrowLeft className="h-4 w-4" />
    </button>
  );

  const card = (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={`critical-toast-${notification.id}-title`}
      aria-describedby={`critical-toast-${notification.id}-body`}
      data-critical-toast
      data-notification-id={notification.id}
      className={wrapperClassName}
    >
      <BottomSheetShell
        ref={containerRef}
        // Desktop renders inside the floating top-left stack column, so the
        // card needs full rounding on all four corners (`rounded`). The
        // legacy `docked-sm-rounded` keeps the mobile look (rounded top
        // corners only, flush to the bottom edge) intact.
        radiusMode={desktopTarget ? 'rounded' : 'docked-sm-rounded'}
        showHandle
        handleMobileOnly
        safeAreaBottom
        contentClassName=""
      >
        {header}

        {isPayment ? (
          <div className="px-4 pt-3 pb-4 flex flex-wrap items-center gap-1.5">
            {picker === 'actions' && (
              <>
                <button
                  type="button"
                  data-action="approve"
                  disabled={busy !== null}
                  onClick={() => take('approve_once')}
                  className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50"
                >
                  {busy === 'approve_once'
                    ? t('criticalToast.approving')
                    : `Approve${amount ? ` ${formatCredits(amount)}` : ''}`}
                </button>
                <button
                  type="button"
                  data-action="set_limit"
                  disabled={busy !== null}
                  onClick={() => setPicker('quick')}
                  className="px-3 py-1.5 rounded-md bg-emerald-50 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs font-medium disabled:opacity-50"
                >
                  {t('notifications.setLimit')}
                </button>
                <button
                  type="button"
                  data-action="deny"
                  disabled={busy !== null}
                  onClick={() => take('deny')}
                  className="px-3 py-1.5 rounded-md bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-200 text-xs font-medium disabled:opacity-50"
                >
                  {busy === 'deny' ? t('criticalToast.denying') : t('notifications.deny')}
                </button>
                {chatId && onView && (
                  <button
                    type="button"
                    data-action="view"
                    onClick={onView}
                    className="ml-auto px-2 py-1.5 rounded-md text-xs text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                  >
                    {t('criticalToast.openChat')}
                  </button>
                )}
              </>
            )}

            {picker === 'quick' && (
              // Quick-pick sub-row. Back arrow sits inline at the left
              // so the row stays single-line. Multipliers resolve
              // against `pickerBase` — the daily limit when known
              // (much more useful than the trivial per-request amount).
              <>
                {backButton('actions')}
                <button
                  type="button"
                  data-action="set_limit_2x"
                  disabled={busy !== null || pickerBase <= 0}
                  onClick={() => take('always_approve', quick2x)}
                  className="px-3 py-1.5 rounded-md bg-emerald-50 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs font-medium disabled:opacity-50"
                  title={`Auto-approve up to ${fmtMoney(quick2x)} per request`}
                >
                  2x · {fmtMoney(quick2x)}
                </button>
                <button
                  type="button"
                  data-action="set_limit_10x"
                  disabled={busy !== null || pickerBase <= 0}
                  onClick={() => take('always_approve', quick10x)}
                  className="px-3 py-1.5 rounded-md bg-emerald-50 dark:bg-emerald-500/10 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs font-medium disabled:opacity-50"
                  title={`Auto-approve up to ${fmtMoney(quick10x)} per request`}
                >
                  10x · {fmtMoney(quick10x)}
                </button>
                <button
                  type="button"
                  data-action="set_limit_custom"
                  disabled={busy !== null}
                  onClick={() => setPicker('custom')}
                  className="px-3 py-1.5 rounded-md bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-200 text-xs font-medium disabled:opacity-50"
                >
                  Custom…
                </button>
              </>
            )}

            {picker === 'custom' && (
              // Custom-amount sub-row. Back arrow inline at left,
              // labelled input + Apply on the right. Enter submits,
              // Esc returns to the quick row.
              <form
                className="flex w-full items-center gap-1.5"
                onSubmit={(e) => {
                  e.preventDefault();
                  const n = parseFloat(customStr);
                  if (!Number.isFinite(n) || n <= 0) return;
                  take('always_approve', n);
                }}
              >
                {backButton('quick')}
                <span className="text-xs text-zinc-500 dark:text-zinc-400">
                  Up to
                </span>
                <div className="inline-flex items-center rounded-md bg-zinc-100 dark:bg-zinc-800 px-2">
                  <input
                    ref={customInputRef}
                    type="number"
                    inputMode="decimal"
                    min={amountNum > 0 ? amountNum : 0.01}
                    step="0.01"
                    value={customStr}
                    onChange={(e) => setCustomStr(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        setPicker('quick');
                      }
                    }}
                    placeholder={
                      pickerBase > 0
                        ? pickerBase.toFixed(2)
                        : amountNum > 0
                          ? amountNum.toFixed(2)
                          : '0.10'
                    }
                    disabled={busy !== null}
                    className="bg-transparent px-1 py-1 text-xs w-20 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">{t('settings.creditsUnit')}</span>
                </div>
                <button
                  type="submit"
                  data-action="set_limit_apply"
                  disabled={
                    busy !== null ||
                    !Number.isFinite(parseFloat(customStr)) ||
                    parseFloat(customStr) <= 0
                  }
                  className="ml-auto px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50"
                >
                  {busy === 'always_approve' ? t('criticalToast.setting') : t('content.apply')}
                </button>
              </form>
            )}
          </div>
        ) : null}

        {isToolApproval ? (
          <div className="px-4 pt-3 pb-4 flex flex-wrap gap-1.5">
            <button
              type="button"
              data-action="approve"
              disabled={busy !== null}
              onClick={() => take('approve')}
              className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50"
            >
              {busy === 'approve' ? t('criticalToast.approving') : t('notifications.approve')}
            </button>
            <button
              type="button"
              data-action="reject"
              disabled={busy !== null}
              onClick={() => take('reject')}
              className="px-3 py-1.5 rounded-md bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-200 text-xs font-medium disabled:opacity-50"
            >
              {busy === 'reject' ? t('criticalToast.rejecting') : t('workspace.ssh.trust.reject')}
            </button>
          </div>
        ) : null}

        {isPublishRequest ? (
          <div className="px-4 pt-3 pb-4 flex flex-wrap gap-1.5">
            <button
              type="button"
              data-action="approve"
              disabled={busy !== null}
              onClick={() => take('approve')}
              className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50"
            >
              {busy === 'approve' ? t('criticalToast.publishing') : t('criticalToast.publishApp')}
            </button>
            <button
              type="button"
              data-action="deny"
              disabled={busy !== null}
              onClick={() => take('deny')}
              className="px-3 py-1.5 rounded-md bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-200 text-xs font-medium disabled:opacity-50"
            >
              {busy === 'deny' ? t('criticalToast.denying') : t('criticalToast.dontPublish')}
            </button>
          </div>
        ) : null}

        {isPermissionRequest ? (
          <div className="px-4 pt-3 pb-4 flex flex-wrap gap-1.5">
            <button
              type="button"
              data-action="approve"
              disabled={busy !== null}
              onClick={() => take('approve')}
              className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50"
            >
              {busy === 'approve'
                ? 'Granting…'
                : `Allow${permissionFeature || permissionAgentName ? ` ${permissionFeature ?? permissionAgentName}` : ''}`}
            </button>
            <button
              type="button"
              data-action="deny"
              disabled={busy !== null}
              onClick={() => take('deny')}
              className="px-3 py-1.5 rounded-md bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-200 text-xs font-medium disabled:opacity-50"
            >
              {busy === 'deny' ? t('criticalToast.denying') : t('notifications.deny')}
            </button>
          </div>
        ) : null}
      </BottomSheetShell>
    </div>
  );

  // Desktop: portal into the shared top-left toast column (above
  // any non-critical prompts). Mobile / pre-hydration: render in
  // place so the docked bottom sheet retains its position fixed
  // styling above the safe area.
  if (desktopTarget) {
    return <ToastStackPortal slot="critical">{card}</ToastStackPortal>;
  }
  return card;
}
